import {
  ArrowLeftIcon,
  Badge,
  Banner,
  Container,
  Row,
  Col,
} from '@paypalcorp/pp-react'
import { navigate } from '@reach/router'
import { isEmpty } from 'lodash'
import React, { FunctionComponent, useEffect } from 'react'
import { CodeSelector } from '../../components/integration-builder'
import { CheckoutSimulatorProvider } from '../../components/integration-builder/context/CheckoutSimulatorProvider'
import { useApplicationContext } from '../../provider'
import withAnalytics from '../../utils/withAnalytics'
import Layout from '../layout'
import styles from './integration-builder.module.css'
import Build from './sections/Build'
import Configure from './sections/Configure'
import Demo from './sections/Demo'
import Preview from './sections/Preview'
import TakeLive from './sections/TakeLive'
import Code from './sections/Code'
import HelpModal from './sections/HelpModal'
import KnowBeforeYouCode from './sections/KnowBeforeYouCode'
import ArticleFeedback from '../../components/article-feedback'
import { removeGAParam } from '../../components/pp-react-wrappers'

const CheckoutSimulatorNew: FunctionComponent = ({}) => {
  const { user, experiments } = useApplicationContext()
  const isInIntBuilderExperiment =
    experiments?.integrationBuilder?.startsWith('Trmt_') || false
  const userLoggedOut = !user?.loggedIn
  const fptiData = typeof window !== `undefined` && window.fpti

  let options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }

  const lastUpdated =
    typeof document !== 'undefined'
      ? new Intl.DateTimeFormat('en-US', options)
          .format(new Date(document.lastModified))
          .replace(',', '')
          .replace(' at ', ', @ ')
          .replace('AM', 'am')
          .replace('PM', 'pm')
      : ''

  // ANALYTICS WHEN PAGE LOADED
  useEffect(() => {
    if (fptiData && window.PAYPAL?.analytics?.instance) {
      window.PAYPAL.analytics.instance.record({
        data: {
          ...fptiData,
          page: 'main:developer:integration-builder',
          pgrp: 'main:developer:integration-builder',
          e: 'page-load',
          page_name: fptiData && fptiData.page.replace(/:/g, '_'),
          event_name:
            fptiData && `${fptiData.page.replace(/:/g, '_')}_page_load`,
          pu: removeGAParam(window.location.href),
        },
      })
    }
  })

  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1)
    } else {
      navigate('/home/')
    }
  }

  return isInIntBuilderExperiment ? (
    <>
      <Layout
        inverseHeaderScheme={true}
        title="Integration Builder - Button Generator - PayPal Developer"
        description="Generate the integration for your own PayPal button for your websites with a few simple clicks with our integration builder here."
        keywords="paypal generator, integration builder, paypal button generator, paypal button creator, generator paypal, paypal builder, checkout builder"
      >
        <Banner
          type="warning"
          className={styles.checkout_simulator__banner}
          title="This tool is not optimized for mobile experience. If something isn't working on mobile, try switching to a desktop computer."
        />
        <CheckoutSimulatorProvider>
          <div
            className={`${styles.checkout_simulator__header} ${
              !userLoggedOut
                ? styles.checkout_simulator__header_loggedIn
                : styles.checkout_simulator__header_loggedOut
            }`}
          >
            <div className={styles.checkout_simulator__go_back_wrapper}>
              <button
                aria-label="Go Back"
                className={styles.checkout_simulator__go_back}
                onClick={handleGoBack}
              >
                <ArrowLeftIcon size="sm" />
                <span className={styles.checkout_simulator__go_back_title}>
                  Back
                </span>
              </button>
            </div>
            <div className={styles.checkout_simulator__header_title}>
              <h1>Integration Builder</h1>
              <HelpModal />
            </div>
            <div className={styles.checkout_simulator__header_badge_wrapper}>
              <Badge type="feature">JS SDK</Badge>
              <p className={styles.checkout_simulator__update_date}>
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
          <KnowBeforeYouCode />
          <CodeSelector />
          <div className={styles.checkout_simulator__container_content}>
            <div className={styles.checkout_simulator__left_section}>
              <Configure />
              <Build />
              <Demo />
              <TakeLive />
            </div>
            <div className={styles.checkout_simulator__right_section}>
              <Preview />
              <Code />
            </div>
          </div>
          <ArticleFeedback />
        </CheckoutSimulatorProvider>
      </Layout>
    </>
  ) : (
    <Layout
      inverseHeaderScheme={true}
      title="Integration Builder - Button Generator - PayPal Developer"
      description="Generate the integration for your own PayPal button for your websites with a few simple clicks with our integration builder here."
      keywords="paypal generator, integration builder, paypal button generator, paypal button creator, generator paypal, paypal builder, checkout builder"
    >
      <div className="integrationBuilder_warning">
        <Container>
          <Row>
            <Col>
              <p className="integrationBuilder_disclaimer">
                Integration Builder is currently available in US only. We will
                be expanding this tool globally shortly.
              </p>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  )
}

export default withAnalytics(CheckoutSimulatorNew)
